import moment from "moment/moment";
import router from "@/router";
import Loader from "@/components/loader/Loader.vue";
import {FacilitySettingsModel} from "@/models/FacilitySettings";
import {percentFromNumber} from "@/utils/percentFromNumber";
import {countByKeys} from "@/utils/countByKeys";
import {getWeekDaysFromDateRange} from "@/utils/dates";
import {MaintenanceMixin} from "@/mixins/Maintenance";
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";

export default {
  name: "ReportScheduledMaintenance",
  mixins: [MaintenanceMixin],
  data() {
    return {
      loading: true,
      period: "week",
      startDate: moment().subtract(1, "week").toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      reportData: null,
      picker: {
        start: false,
        end: false
      },
      periods: [
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Week"], value: "week" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Month"], value: "month" },
        { title: this.$i18n.messages[this.$i18n.locale]["GeneralTranslations.Year"], value: "year" }
      ],
      assignedRequestsLimit: 5,
      requestsByHour: [],
      requestsByDate: [],
      dailyCount: [],
      timeFormat: null,
      headers: [
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Resident'],
          align: 'start',
          value: 'residentName',
        },
        {
          text: '',
          align: '',
          sortable: false,
          width: 40,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['MaintenanceList.RequestType'],
          align: 'start',
          sortable: false,
          value: 'typeId',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Category'],
          value: 'category',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Status'],
          value: 'status',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['MaintenanceView.RequestDate'],
          align: 'start',
          value: 'dateTime',
        },
      ],
      search: ""
    }
  },
  components: {
    Loader
  },
  computed: {
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat)
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat)
    },
    servicesByStatus() {
      return countByKeys(this.reportData.requests, "status");
    },
    maintenanceTypes() {
      return this.$store.state.maintenanceModule.maintenanceTypes;
    }
  },
  watch: {
    "reportData"(report) {
      this.requestsByDate = this.groupByDay(report.requests)
      this.dailyCount = this.getDailyCount(report.requests)
      this.requestsByHour = this.groupByHour(report.requests)
    }
  },
  methods: {
    routeToReports() {
      this.$router.push("/reports");
    },
    async handleChangePeriod(period) {
      this.startDate = moment()
        .subtract(1, period)
        .toISOString()
        .substring(0, 10);

      this.endDate = moment()
        .toISOString()
        .substring(0, 10);

      await this.loadReport();
    },
    async loadReport() {
      this.loading = true
      this.reportData = await this.$store.dispatch("getMaintenanceReport", { startDate: moment(this.startDate).startOf('day').toDate(), endDate: moment(this.endDate).endOf('day').toDate() });
      this.loading = false
    },
    calculateRequestsPercentByType(name: string) {
      const percent = percentFromNumber(
        this.reportData.requests.length,
        this.servicesByStatus[name]
      );

      return isNaN(percent) ? 0 : percent.toFixed(2);
    },
    calculateRequestsByStatus(status: string) {
      return this.reportData?.requests?.filter(x => x.status === status)?.length;
    },
    showMoreAssignedRequests() {
      if(this.assignedRequestsLimit === 5) {
        this.assignedRequestsLimit = this.reportData.assignedRequests.length;
      } else {
        this.assignedRequestsLimit = 5;
      }
    },
    getDailyCount(events) {
      const types = events.reduce(function (totals, event) {
        const date = moment(event.dateTime.seconds * 1000)
          .startOf("day")
          .format("DD/MM");
        !totals[date] ? (totals[date] = 1) : (totals[date] = totals[date] + 1);
        return totals;
      }, {});
      return types;
    },
    groupByHour(events) {
      const types = events.reduce((totals, event) => {
        const date = moment(event.dateTime.seconds * 1000);
        const hour = date.clone().minute() >= 59 ? date.add(1, 'hour').format("HH") : date.format("HH"); // Заокруглення часу
        totals[hour] = (totals[hour] || 0) + 1;
        return totals;
      }, {});

      return Object.fromEntries(
        Object.entries(types)
          .map(([key, value]) => [parseInt(key, 10), value])
          .sort(([keyA]: any, [keyB]: any) => keyA - keyB)
      );
    },
    groupByDay(requests) {
      const weekDays = getWeekDaysFromDateRange(moment(this.startDate), moment(this.endDate))
      const week = weekDays.reduce((o, key) => ({ ...o, [key]: undefined }), {})

      const types = requests.reduce(function (totals, event) {
        const dayOfWeek = moment(event.dateTime.seconds * 1000).format("dddd");
        !week[dayOfWeek] ? (week[dayOfWeek] = 1) : (week[dayOfWeek] = totals[dayOfWeek] + 1);
        return week;
      }, {});
      return types;
    },
    handleClickMaintenanceRequest(row) {
      router.push('/maintenanceRequests/' + row.id.toString());
    },
    getMaintenanceType(row) {
      return this.maintenanceTypes.find(x => x.id === row.typeId)?.name;
    },
    getMaintenanceStatus(row) {
      switch (row.status) {
        case MaintenanceRequestStatus.New: {
          return this.$t("MaintenanceList.New");
        }
        case MaintenanceRequestStatus.InProgress: {
          return this.$t("MaintenanceList.InProgress");
        }
        case MaintenanceRequestStatus.Cancelled: {
          return this.$t("MaintenanceList.Cancelled");
        }
        case MaintenanceRequestStatus.Complete: {
          return this.$t("MaintenanceList.Completed");
        }
      }
    },
    chartMessages(): any {
      return { empty: this.$t('GeneralTranslations.NoData') };
    },
    integerYAxisLibrary(): any {
      return {
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                stepSize: 1,
                precision: 0,
                callback: value => {
                  if (Number.isInteger(value)) {
                    return value;
                  }
                }
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                callback: value => {
                  if (Number.isInteger(value)) {
                    return value;
                  }
                },
                stepSize: 1
              }
            }
          ]
        }
      };
    }
  },
  async created() {
    await this.$store.dispatch("getFacility");
    await this.$store.dispatch("getMaintenanceTypes");
    this.timeFormat = this.facility.timeFormat.includes('A')?'hh  A':'HH'
    await this.loadReport();
  }
}